import { defineStore } from 'pinia';
import { ref } from 'vue';
import { toast } from 'vue-sonner';
import { inventoryAiAnalysisActive, showInventoryAiAnalysis } from '@/api/inventoryAi';

// Matches the backend ProcessInventoryAiAnalysisJob::staleAfterSeconds()
// (MAX_ATTEMPTS(1) × 300s job timeout + 60s grace = 360s) so the banner can
// never outlive a job's max lifetime even if every terminal signal is lost.
const STALE_TIMEOUT_MS = 6 * 60 * 1000; // 6 minutes

// localStorage key holding all tracked analyses so banners survive a full page
// refresh. Removed when nothing is tracked. The v1 single-job key is migrated
// (and always deleted) on restore().
const STORAGE_KEY = 'inventoryAiProcessing:activeMoves';
const LEGACY_STORAGE_KEY = 'inventoryAiProcessing:active';

// localStorage key recording uploads still in flight (no job id yet) so a full
// page reload that kills them can be reported to the user afterwards. Uploads
// exist only in browser memory — no AiJob exists server-side until the upload
// completes — so they cannot be resumed, only reported. A sessionStorage tab id
// distinguishes "this tab reloaded mid-upload" from "another tab is uploading".
const PENDING_UPLOADS_KEY = 'inventoryAiProcessing:pendingUploads';
const TAB_ID_KEY = 'inventoryAiProcessing:tabId';

// Fallback polling cadence — only runs while the realtime connection is down.
const POLL_BASE_MS = 4000;
const POLL_MAX_MS = 15000;
const POLL_JITTER_MS = 1500;

type AnalysisStatus = 'pending' | 'processing' | 'completed' | 'failed' | 'skipped';

/**
 * Banner phase for one move: 'uploading' while media is being uploaded to S3
 * (before a job id exists), then 'analysing' once at least one job is
 * dispatched and no upload is in flight.
 */
type ProcessingPhase = 'uploading' | 'analysing';

interface TrackedJob {
    /** Hard expiry (ms epoch) for the backstop. */
    expiresAt: number;
    /** Room being analysed, shown in the banner label; null when unknown. */
    roomName: string | null;
}

interface MoveEntry {
    /** The move id as handed to start()/track(), for API paths and display. */
    moveId: number | string;
    /** In-flight uploads that have not returned a job id yet. Never persisted. */
    uploads: number;
    /** Backstop expiry for a hung upload; set on each start(). */
    uploadExpiresAt: number | null;
    /** Room per in-flight upload, keyed by upload token. Never persisted. */
    uploadRooms: Map<string, string | null>;
    /** Outstanding analysis jobs. Persisted. */
    jobs: Map<number, TrackedJob>;
}

/** In-flight upload recorded in localStorage so a reload that kills it can be reported. */
interface PendingUploadMarker {
    moveId: number | string;
    roomName: string | null;
    tabId: string;
    startedAt: number;
}

/** Job values persisted as objects; plain numbers are accepted on restore (older format). */
type PersistedJobValue = { expiresAt: number; roomName?: string | null } | number;

interface PersistedMove {
    moveId: number | string;
    jobs: Record<string, PersistedJobValue>;
}

/**
 * Tracks which AI inventory analyses are running in the background so the
 * inventory page can show a processing banner. Written by InventoryAiPanel.vue
 * across the upload/analysis lifecycle; read by InventoryAiHeaderIndicator.vue.
 *
 * The store is the source of truth for the banner: once a job is tracked it can
 * confirm terminal state independently of any component, so the indicator stays
 * correct even when the panel (and its Echo channel listener) is unmounted —
 * e.g. the user navigates away mid-analysis. Echo realtime remains the fast
 * path; polling only kicks in as a fallback when the realtime connection drops.
 *
 * Keyed model: entries are keyed by move, and each move holds the set of its
 * outstanding jobs (a batch retry queues one job per room) plus an in-flight
 * upload counter. Analyses for different moves are fully independent — the
 * banner on one order's inventory page reflects only that order's move, and
 * finishing one move's jobs never touches another's. Every job carries its own
 * hard expiry so a stuck job can't pin the banner and a late-tracked job can't
 * extend an earlier one's window. Tracked jobs are mirrored to localStorage so
 * a full page refresh restores every banner and resumes reconciling against
 * the backend.
 */
export const useInventoryAiProcessingStore = defineStore('inventoryAiProcessing', () => {
    const activeMoves = ref(new Map<string, MoveEntry>());

    const backstopTimers = new Map<string, ReturnType<typeof setTimeout>>();
    let pollRunId = 0;
    let pollController: AbortController | null = null;
    let connectedCallback: (() => void) | null = null;
    let disconnectedCallback: (() => void) | null = null;

    const keyOf = (moveId: number | string): string => String(moveId);

    const sleep = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

    const backoff = (attempt: number): number => {
        const base = Math.min(POLL_BASE_MS * attempt, POLL_MAX_MS);
        const jitter = Math.floor(Math.random() * POLL_JITTER_MS);
        return Math.max(POLL_BASE_MS, base + jitter);
    };

    const isEchoConnected = (): boolean => window.echo?.connector?.pusher?.connection?.state === 'connected';

    function isProcessingForMove(moveId: number | string | null | undefined): boolean {
        return moveId != null && activeMoves.value.has(keyOf(moveId));
    }

    function phaseForMove(moveId: number | string | null | undefined): ProcessingPhase {
        const entry = moveId != null ? activeMoves.value.get(keyOf(moveId)) : undefined;
        return entry && entry.uploads > 0 ? 'uploading' : 'analysing';
    }

    /**
     * Room names being analysed for a move, in tracking order without
     * duplicates. Jobs with an unknown room (e.g. restored from the older
     * persistence format) are skipped — the banner falls back to a generic
     * label when the list is empty.
     */
    function roomsForMove(moveId: number | string | null | undefined): string[] {
        const entry = moveId != null ? activeMoves.value.get(keyOf(moveId)) : undefined;
        const rooms: string[] = [];

        entry?.jobs.forEach((job) => {
            if (job.roomName && !rooms.includes(job.roomName)) {
                rooms.push(job.roomName);
            }
        });

        return rooms;
    }

    /**
     * Room names with an upload still in flight for a move, in start order
     * without duplicates. Lets the panel attribute the upload phase to the
     * room that actually started it instead of whichever room is selected.
     */
    function uploadingRoomsForMove(moveId: number | string | null | undefined): string[] {
        const entry = moveId != null ? activeMoves.value.get(keyOf(moveId)) : undefined;
        const rooms: string[] = [];

        entry?.uploadRooms.forEach((roomName) => {
            if (roomName && !rooms.includes(roomName)) {
                rooms.push(roomName);
            }
        });

        return rooms;
    }

    /**
     * Whether a specific room of a move has an in-flight upload or an
     * outstanding analysis job. Drives the per-room disabling of the upload
     * area: other rooms of the same move (and every other move) stay
     * unaffected. Jobs with an unknown room disable no room.
     */
    function isRoomProcessingForMove(
        moveId: number | string | null | undefined,
        roomName: string | null | undefined,
    ): boolean {
        if (!roomName || !isProcessingForMove(moveId)) {
            return false;
        }

        return uploadingRoomsForMove(moveId).includes(roomName) || roomsForMove(moveId).includes(roomName);
    }

    function outstandingJobs(): Array<{ moveId: number | string; jobId: number }> {
        const pairs: Array<{ moveId: number | string; jobId: number }> = [];

        activeMoves.value.forEach((entry) => {
            entry.jobs.forEach((_expiresAt, jobId) => {
                pairs.push({ moveId: entry.moveId, jobId });
            });
        });

        return pairs;
    }

    function persist(): void {
        try {
            const moves: Record<string, PersistedMove> = {};

            activeMoves.value.forEach((entry, key) => {
                if (entry.jobs.size === 0) {
                    return;
                }

                const jobs: Record<string, PersistedJobValue> = {};
                entry.jobs.forEach((job, jobId) => {
                    jobs[String(jobId)] = { expiresAt: job.expiresAt, roomName: job.roomName };
                });

                moves[key] = { moveId: entry.moveId, jobs };
            });

            if (Object.keys(moves).length === 0) {
                localStorage.removeItem(STORAGE_KEY);
            } else {
                localStorage.setItem(STORAGE_KEY, JSON.stringify({ version: 2, moves }));
            }
        } catch {
            // Ignore storage failures (private mode / quota) — in-memory tracking still works.
        }
    }

    function getTabId(): string {
        try {
            let id = sessionStorage.getItem(TAB_ID_KEY);

            if (!id) {
                id =
                    typeof crypto?.randomUUID === 'function' ?
                        crypto.randomUUID()
                    :   `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
                sessionStorage.setItem(TAB_ID_KEY, id);
            }

            return id;
        } catch {
            return 'unknown-tab';
        }
    }

    function readPendingUploads(): Record<string, PendingUploadMarker> {
        try {
            const raw = localStorage.getItem(PENDING_UPLOADS_KEY);
            const parsed = raw ? JSON.parse(raw) : null;

            return parsed && typeof parsed === 'object' ? parsed : {};
        } catch {
            return {};
        }
    }

    function writePendingUploads(markers: Record<string, PendingUploadMarker>): void {
        try {
            if (Object.keys(markers).length === 0) {
                localStorage.removeItem(PENDING_UPLOADS_KEY);
            } else {
                localStorage.setItem(PENDING_UPLOADS_KEY, JSON.stringify(markers));
            }
        } catch {
            // Ignore storage failures — the reload notice is best-effort.
        }
    }

    function addPendingUpload(moveId: number | string, roomName: string | null): string {
        const token = `${keyOf(moveId)}:${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
        const markers = readPendingUploads();

        markers[token] = { moveId, roomName, tabId: getTabId(), startedAt: Date.now() };
        writePendingUploads(markers);

        return token;
    }

    function removePendingUpload(moveId: number | string, token: string | null): void {
        const markers = readPendingUploads();

        if (token && markers[token]) {
            delete markers[token];
            writePendingUploads(markers);

            return;
        }

        // No token (legacy caller): drop the newest marker this tab holds for the move.
        const key = keyOf(moveId);
        const own = Object.entries(markers)
            .filter(([, marker]) => keyOf(marker.moveId) === key && marker.tabId === getTabId())
            .sort(([, a], [, b]) => b.startedAt - a.startedAt);

        if (own.length > 0) {
            delete markers[own[0][0]];
            writePendingUploads(markers);
        }
    }

    /**
     * Report uploads this tab lost to a full page reload. A dead upload cannot
     * be resumed — the honest recovery is telling the user to re-run the
     * analysis. Markers from other tabs are left alone (their upload may still
     * be running) unless past the stale window, when they are garbage-collected
     * silently. Runs once at store creation; the store is created lazily, so a
     * reload onto a non-inventory page surfaces the notice when the user next
     * opens an inventory page.
     */
    function reportInterruptedUploads(): void {
        const markers = readPendingUploads();
        const ownTabId = getTabId();
        const now = Date.now();
        const interrupted: PendingUploadMarker[] = [];
        let changed = false;

        Object.entries(markers).forEach(([token, marker]) => {
            const fresh = typeof marker?.startedAt === 'number' && now - marker.startedAt < STALE_TIMEOUT_MS;

            if (marker?.tabId === ownTabId) {
                // This tab just (re)loaded, so any upload it still owned is dead.
                if (fresh) {
                    interrupted.push(marker);
                }

                delete markers[token];
                changed = true;
            } else if (!fresh) {
                delete markers[token];
                changed = true;
            }
        });

        if (changed) {
            writePendingUploads(markers);
        }

        if (interrupted.length === 0) {
            return;
        }

        const rooms = Array.from(
            new Set(interrupted.map((marker) => marker.roomName).filter((room): room is string => !!room)),
        );
        const subject = rooms.length > 0 ? `The media upload for ${rooms.join(', ')}` : 'A media upload';

        toast.warning(
            `${subject} was interrupted by the page reload. Please re-select the files and run the analysis again.`,
            {
                duration: 10_000,
            },
        );
    }

    function bindEcho(): void {
        const connection = window.echo?.connector?.pusher?.connection;

        if (!connection || connectedCallback) {
            return;
        }

        connectedCallback = () => {
            if (activeMoves.value.size === 0) {
                return;
            }

            // Realtime is healthy again — stop polling and reconcile once in case
            // a terminal event was missed while the connection was down.
            abortPolling();

            void reconcileAllThenMaybePoll();
        };

        disconnectedCallback = () => {
            if (activeMoves.value.size === 0) {
                return;
            }

            startPolling();
        };

        connection.bind('connected', connectedCallback);
        connection.bind('disconnected', disconnectedCallback);
    }

    function unbindEcho(): void {
        const connection = window.echo?.connector?.pusher?.connection;

        if (connection && connectedCallback) {
            connection.unbind('connected', connectedCallback);
        }

        if (connection && disconnectedCallback) {
            connection.unbind('disconnected', disconnectedCallback);
        }

        connectedCallback = null;
        disconnectedCallback = null;
    }

    function abortPolling(): void {
        pollRunId++;

        if (pollController) {
            pollController.abort();
            pollController = null;
        }
    }

    function clearBackstop(key: string): void {
        const timer = backstopTimers.get(key);

        if (timer) {
            clearTimeout(timer);
            backstopTimers.delete(key);
        }
    }

    function removeEntry(key: string): void {
        clearBackstop(key);
        activeMoves.value.delete(key);
        persist();

        if (activeMoves.value.size === 0) {
            abortPolling();
            unbindEcho();
        }
    }

    /**
     * Self-heal if every terminal signal for a move is lost (realtime event
     * never delivered and the worker died before it could broadcast a failure).
     * Each job carries its own expiry, so the timer fires at the soonest one,
     * prunes only what has actually expired, and re-arms for the rest.
     */
    function armBackstop(key: string): void {
        clearBackstop(key);

        const entry = activeMoves.value.get(key);

        if (!entry) {
            return;
        }

        let fireAt = Number.POSITIVE_INFINITY;

        entry.jobs.forEach((job) => {
            fireAt = Math.min(fireAt, job.expiresAt);
        });

        if (entry.uploads > 0 && entry.uploadExpiresAt !== null) {
            fireAt = Math.min(fireAt, entry.uploadExpiresAt);
        }

        if (!Number.isFinite(fireAt)) {
            return;
        }

        const timer = setTimeout(
            () => {
                backstopTimers.delete(key);

                const current = activeMoves.value.get(key);

                if (!current) {
                    return;
                }

                const now = Date.now();
                let changed = false;

                current.jobs.forEach((job, jobId) => {
                    if (job.expiresAt <= now) {
                        current.jobs.delete(jobId);
                        changed = true;
                    }
                });

                if (current.uploads > 0 && current.uploadExpiresAt !== null && current.uploadExpiresAt <= now) {
                    current.uploads = 0;
                    current.uploadExpiresAt = null;
                    current.uploadRooms.clear();
                }

                if (changed) {
                    persist();
                }

                if (current.uploads === 0 && current.jobs.size === 0) {
                    removeEntry(key);
                } else {
                    armBackstop(key);
                }
            },
            Math.max(0, fireAt - Date.now()),
        );

        backstopTimers.set(key, timer);
    }

    function ensureEntry(moveId: number | string): MoveEntry {
        const key = keyOf(moveId);

        if (!activeMoves.value.has(key)) {
            activeMoves.value.set(key, {
                moveId,
                uploads: 0,
                uploadExpiresAt: null,
                uploadRooms: new Map(),
                jobs: new Map(),
            });
            bindEcho();
        }

        return activeMoves.value.get(key) as MoveEntry;
    }

    /**
     * One-shot status check for a single job. Removes the job (via finish())
     * and returns true when it has reached a terminal state. A transient or
     * network error leaves the job tracked — the backstop remains the guard.
     */
    async function syncJobOnce(moveId: number | string, jobId: number, signal?: AbortSignal): Promise<boolean> {
        try {
            const { data } = await showInventoryAiAnalysis(moveId, jobId, signal ? { signal } : undefined);
            const status = data?.status as AnalysisStatus | undefined;

            if (status === 'completed' || status === 'failed' || status === 'skipped') {
                finish(moveId, jobId);

                return true;
            }

            return false;
        } catch {
            return false;
        }
    }

    /**
     * Reconcile every outstanding job once, then start the polling fallback
     * only if realtime is unavailable. Shared by restore() and the Echo
     * reconnect handler.
     */
    async function reconcileAllThenMaybePoll(): Promise<void> {
        for (const { moveId, jobId } of outstandingJobs()) {
            await syncJobOnce(moveId, jobId);
        }

        if (outstandingJobs().length > 0 && !isEchoConnected()) {
            startPolling();
        }
    }

    /**
     * Single polling loop sweeping every outstanding job across all moves,
     * sequentially per cycle with the shared backoff. Jobs are removed
     * individually as they turn terminal; the loop ends when none remain.
     */
    function startPolling(): void {
        if (pollController || isEchoConnected() || outstandingJobs().length === 0) {
            return;
        }

        const runId = ++pollRunId;
        const controller = new AbortController();
        pollController = controller;

        void (async () => {
            try {
                let attempt = 1;

                while (runId === pollRunId) {
                    const pairs = outstandingJobs();

                    if (pairs.length === 0) {
                        return;
                    }

                    for (const { moveId, jobId } of pairs) {
                        if (runId !== pollRunId || controller.signal.aborted) {
                            return;
                        }

                        await syncJobOnce(moveId, jobId, controller.signal);
                    }

                    // Transient (429/5xx) or one-off network errors keep the loop
                    // alive: a false clear of the banner is worse than a slightly
                    // late one, and the per-job backstop guarantees eventual clearing.
                    await sleep(backoff(attempt++));
                }
            } finally {
                if (pollController === controller) {
                    pollController = null;
                }
            }
        })();
    }

    /**
     * Mark a move's banner active for an upload that has not yet returned a
     * job id. Shows immediately so the user sees feedback during upload;
     * track() takes over once the job id is known. Returns a token identifying
     * this upload's reload marker — hand it back to finishUpload() when the
     * upload settles so an interrupted-by-reload notice is only shown for
     * uploads that actually died.
     */
    function start(moveId: number | string | null | undefined, roomName: string | null = null): string | null {
        if (!moveId) {
            return null;
        }

        const entry = ensureEntry(moveId);
        entry.uploads++;
        entry.uploadExpiresAt = Date.now() + STALE_TIMEOUT_MS;
        armBackstop(keyOf(moveId));

        const token = addPendingUpload(moveId, roomName);
        entry.uploadRooms.set(token, roomName);

        return token;
    }

    /**
     * Settle one in-flight upload for a move (success, cancellation, or
     * failure). The entry disappears once no uploads or jobs remain. The
     * reload marker is removed even when the entry is already gone (e.g. the
     * backstop cleared it) so a settled upload never triggers a reload notice.
     */
    function finishUpload(moveId: number | string | null | undefined, uploadToken: string | null = null): void {
        if (!moveId) {
            return;
        }

        removePendingUpload(moveId, uploadToken);

        const key = keyOf(moveId);
        const entry = activeMoves.value.get(key);

        if (!entry) {
            return;
        }

        entry.uploads = Math.max(0, entry.uploads - 1);

        if (uploadToken) {
            entry.uploadRooms.delete(uploadToken);
        }

        if (entry.uploads === 0) {
            entry.uploadExpiresAt = null;
            entry.uploadRooms.clear();
        }

        if (entry.uploads === 0 && entry.jobs.size === 0) {
            removeEntry(key);
        } else {
            armBackstop(key);
        }
    }

    /**
     * Begin tracking a dispatched job. Realtime (owned by the panel) is the
     * fast path; this attaches a connection-aware polling fallback that
     * survives navigation, plus an immediate reconcile to close the race where
     * the job finishes before tracking starts. Tracking a job never disturbs
     * other jobs or other moves.
     */
    function track(
        moveId: number | string | null | undefined,
        jobId: number | null | undefined,
        roomName: string | null = null,
    ): void {
        if (!moveId || !jobId) {
            return;
        }

        const entry = ensureEntry(moveId);
        entry.jobs.set(jobId, { expiresAt: Date.now() + STALE_TIMEOUT_MS, roomName });
        persist();
        armBackstop(keyOf(moveId));

        void (async () => {
            const reachedTerminal = await syncJobOnce(moveId, jobId);

            if (!reachedTerminal && !isEchoConnected()) {
                startPolling();
            }
        })();
    }

    /** Remove one finished job; the move's entry (and banner) clears once nothing is outstanding. */
    function finish(moveId: number | string | null | undefined, jobId: number): void {
        if (!moveId) {
            return;
        }

        const key = keyOf(moveId);
        const entry = activeMoves.value.get(key);

        if (!entry || !entry.jobs.delete(jobId)) {
            return;
        }

        persist();

        if (entry.uploads === 0 && entry.jobs.size === 0) {
            removeEntry(key);
        } else {
            armBackstop(key);
        }
    }

    /** Force-clear one move's entry (backstop path and tests). */
    function stop(moveId: number | string | null | undefined): void {
        if (!moveId) {
            return;
        }

        removeEntry(keyOf(moveId));
    }

    /**
     * Reconcile one move's jobs against the backend on demand — used when the
     * panel (re)mounts, e.g. the user returns to the inventory page after
     * navigating away mid-analysis and a terminal event was delivered while
     * the listener was unmounted.
     */
    function recheck(moveId: number | string | null | undefined): void {
        if (!moveId) {
            return;
        }

        const entry = activeMoves.value.get(keyOf(moveId));

        if (!entry) {
            return;
        }

        entry.jobs.forEach((_expiresAt, jobId) => {
            void syncJobOnce(moveId, jobId);
        });
    }

    /**
     * Re-discover analyses that are running for a move from the backend (the
     * source of truth) and track them. Recovers the banner when client-side
     * tracking state was lost — e.g. a full page reload while the upload was
     * still in flight (uploads are never persisted), or an analysis started
     * in another tab or by another user. Called when an inventory page opens.
     */
    async function discoverActive(moveId: number | string | null | undefined): Promise<void> {
        if (!moveId) {
            return;
        }

        try {
            const { data } = await inventoryAiAnalysisActive(moveId);
            const active = Array.isArray(data?.active) ? data.active : [];

            active.forEach((job: { ai_job_id?: number; room_name?: string | null }) => {
                const jobId = Number(job?.ai_job_id);

                if (!jobId || activeMoves.value.get(keyOf(moveId))?.jobs.has(jobId)) {
                    return;
                }

                const roomName = typeof job?.room_name === 'string' && job.room_name !== '' ? job.room_name : null;
                track(moveId, jobId, roomName);
            });
        } catch {
            // Recovery is best-effort — realtime/polling/backstop still govern
            // anything already tracked.
        }
    }

    function readLegacyEntry(): PersistedMove | null {
        let raw: string | null = null;

        try {
            raw = localStorage.getItem(LEGACY_STORAGE_KEY);

            if (raw !== null) {
                localStorage.removeItem(LEGACY_STORAGE_KEY);
            }
        } catch {
            return null;
        }

        if (!raw) {
            return null;
        }

        try {
            const parsed = JSON.parse(raw) as { moveId?: number | string; jobId?: number; expiresAt?: number };
            const expiresAt = typeof parsed?.expiresAt === 'number' ? parsed.expiresAt : 0;

            if (!parsed?.moveId || !parsed?.jobId || Date.now() >= expiresAt) {
                return null;
            }

            return { moveId: parsed.moveId, jobs: { [String(parsed.jobId)]: expiresAt } };
        } catch {
            return null;
        }
    }

    /**
     * Restore tracked jobs from localStorage after a full page refresh. Runs
     * once when the store is first created. Drops jobs past their hard expiry
     * individually so a dead job can never resurrect a zombie banner, then
     * re-arms per-move backstops for the remaining lifetimes and reconciles
     * every job against the backend (some may have finished mid-refresh).
     * Also migrates (and always deletes) the v1 single-job key.
     */
    function restore(): void {
        const persisted: PersistedMove[] = [];
        const legacy = readLegacyEntry();

        if (legacy) {
            persisted.push(legacy);
        }

        try {
            const raw = localStorage.getItem(STORAGE_KEY);

            if (raw) {
                const parsed = JSON.parse(raw) as { version?: number; moves?: Record<string, PersistedMove> };

                Object.values(parsed?.moves ?? {}).forEach((move) => {
                    if (move?.moveId && move.jobs && typeof move.jobs === 'object') {
                        persisted.push(move);
                    }
                });
            }
        } catch {
            try {
                localStorage.removeItem(STORAGE_KEY);
            } catch {
                // Ignore storage failures.
            }
        }

        const now = Date.now();
        let seeded = false;

        persisted.forEach((move) => {
            const jobs = new Map<number, TrackedJob>();

            Object.entries(move.jobs).forEach(([jobId, value]) => {
                // Plain-number values come from the older persisted format
                // (and the legacy v1 key) — accepted with an unknown room.
                const expiresAt = typeof value === 'number' ? value : value?.expiresAt;
                const roomName =
                    typeof value === 'object' && typeof value?.roomName === 'string' ? value.roomName : null;

                if (typeof expiresAt === 'number' && expiresAt > now && Number(jobId)) {
                    jobs.set(Number(jobId), { expiresAt, roomName });
                }
            });

            if (jobs.size === 0) {
                return;
            }

            activeMoves.value.set(keyOf(move.moveId), {
                moveId: move.moveId,
                uploads: 0,
                uploadExpiresAt: null,
                uploadRooms: new Map(),
                jobs,
            });
            armBackstop(keyOf(move.moveId));
            seeded = true;
        });

        persist();

        if (seeded) {
            bindEcho();

            void reconcileAllThenMaybePoll();
        }
    }

    // Resume tracked jobs persisted before a full page refresh.
    restore();

    // Tell the user about any upload a reload in this tab killed mid-flight.
    reportInterruptedUploads();

    return {
        isProcessingForMove,
        isRoomProcessingForMove,
        phaseForMove,
        roomsForMove,
        uploadingRoomsForMove,
        start,
        finishUpload,
        track,
        finish,
        stop,
        recheck,
        discoverActive,
        restore,
    };
});
